// src/pages/MFAVerify/MFAVerifyPage.tsx
import { FC, useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { mfaService } from 'features/mfa';
import './MFAVerifyPage.scss';
import { delete_files } from "features/ai-assistant";
import { UserContext } from "features/account";
import CodeInput from "features/mfa/components/CodeInput/CodeInput";
import Button, { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { MFASetupModal } from "features/mfa";

const MFAVerifyPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userType, setAi_conversation } = useContext(UserContext);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaType, setMfaType] = useState<'email' | 'mobile'>('email');
  const hasInitialized = useRef(false);
  const [mfaEmail, setMfaEmail] = useState<string>('');
  const [showMFASetupModal, setShowMFASetupModal] = useState(false);
  const type = user?.type ?? 'individual'


  useEffect(() => {
    const state = location.state as any;
    if (state?.mfaToken && state?.mfaType) {
      setMfaToken(state.mfaToken);
      setMfaType(state.mfaType);
      setMfaEmail(state.mfaEmail);
      mfaService.resendEmailVerificationCode(state?.mfaEmail).then(r => {});
    } else {
      navigate(-1);
    }
  }, [location, navigate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleVerify = useCallback(async () => {
    if (!code || code.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const request = await mfaService.verifyMFALogin(mfaToken, code, mfaType, type);

      if (request?.type === "individual") {
        await delete_files(user?.clientNumber!);
        localStorage.removeItem("assistantConversation");
        setAi_conversation([]);
        if (request?.mustRedirectToIDAssistant) {
          navigate("/particuliers/id-assistant/add/standard");
        } else {
          navigate("/particuliers/dashboard");
        }
      } else if (request?.type === "partner") {
        if (!!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0)) {
          navigate("/professionnels/base-de-donnees");
        } else {
          navigate("/professionnels/dashboard");
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
    }
  }, [code, mfaToken, mfaType, user, navigate, setAi_conversation]);

  const handleResendCode = useCallback(async () => {
    if (resendTimer > 0 || loading) return;

    setLoading(true);
    setError('');

    try {
      await mfaService.resendEmailVerificationCode(mfaEmail);
      setResendTimer(60);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du renvoi du code');
    } finally {
      setLoading(false);
    }
  }, [resendTimer, loading, mfaEmail]);

  const handleTryAnotherMethod = () => {
    setShowMFASetupModal(true);
  };

  const handleMFASetupClose = () => {
    setShowMFASetupModal(false);
  };

  const handleMFASetupSuccess = () => {
    setShowMFASetupModal(false);
    // Après activation d'une autre méthode, rediriger vers le profil
    navigate('/profil');
  };

  return (
    <>
      <div className="mfa-verify-page">
        <div className="verify-container">
          <div className="verify-card">
            <div className="verify-header">
              <div className="verify-icon"></div>
              <h2>Vérification à deux facteurs</h2>
              <p className="verify-subtitle">
                Un code de vérification a été envoyé à votre adresse email
              </p>
            </div>

            <CodeInput
              value={code}
              onChange={setCode}
              length={6}
              error={error}
              disabled={loading}
              autoFocus={true}
              onComplete={handleVerify}
            />

            <div className="verify-actions d-flex justify-content-center flex-column">
              <Button
                type={Button_Type.PRIMARY}
                style={Button_Style.SOLID}
                label={loading ? "Vérification..." : "Vérifier"}
                onClick={handleVerify}
                disabled={loading || code.length !== 6}
              />

              <div className={"d-flex justify-content-between align-items-center"}>
                <div className="resend-wrapper">
                  <button
                    className="resend-link"
                    onClick={handleResendCode}
                    disabled={resendTimer > 0 || loading}
                  >
                    {resendTimer > 0
                      ? `Renvoyer le code (${resendTimer}s)`
                      : "Renvoyer le code"}
                  </button>
                </div>

                <div className="alternative-method-wrapper">
                  <button
                    className="alternative-method-link"
                    onClick={handleTryAnotherMethod}
                  >
                    Autre méthode
                  </button>
                </div>
              </div>
            </div>

            <div className="verify-help">
              <p className="help-title">Vous n'avez pas reçu le code ?</p>
              <ul className="help-list">
                <li>Vérifiez votre dossier spam</li>
                <li>Assurez-vous que l'adresse email est correcte</li>
                <li onClick={() => {
                  window.open("https://idprotect.fr/contact/", "_blank", "noopener,noreferrer");
                }}>Contactez le support client</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Modal pour choisir une autre méthode MFA */}
      {showMFASetupModal && (
        <MFASetupModal
          isOpen={showMFASetupModal}
          onClose={handleMFASetupClose}
          onSuccess={handleMFASetupSuccess}
          userEmail={mfaEmail}
          fromLogin={true}
          mfaToken={mfaToken}
        />
      )}
    </>
  );
};

export default MFAVerifyPage;